// src/components/SubmitTime/fields/InputDateField.tsx
// Modern React implementation of InputDateField custom field

import React, { useState, useEffect, useRef } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import type { CustomFieldProps } from '../types';
import './InputDateField.css';

interface InputDateFieldProps extends CustomFieldProps {
  autoOpen?: boolean;
}

interface DateInputProps {
  value?: string;
  onClick?: () => void;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
}

const DateInput = React.forwardRef<HTMLInputElement, DateInputProps>(
  ({ value, onClick, onChange, className, placeholder, disabled }, ref) => (
    <div className="submit-time-date-input-wrapper">
      <input
        ref={ref}
        type="text"
        value={value || ''}
        onClick={onClick}
        onChange={onChange || (() => {})}
        className={className}
        placeholder={placeholder}
        readOnly
        disabled={disabled}
      />
      <button
        type="button"
        className="submit-time-date-icon-btn"
        onClick={onClick}
        tabIndex={-1}
        disabled={disabled}
      >
        <CalendarTodayIcon style={{ fontSize: 14 }} />
      </button>
    </div>
  ),
);
DateInput.displayName = 'DateInput';

export const InputDateField: React.FC<InputDateFieldProps> = ({
  customField,
  canEdit,
  handleDynamicInputChange,
  UpdateCustomFields,
  autoOpen,
  showRequiredError,
}) =>
{
  const datepickerRef = useRef<any>(null);
  const parseToDate = (fieldValue: string | number | null | undefined): Date | null =>
  {
    if (!fieldValue || fieldValue === 'Not Set') return null;
    try
    {
      const date = new Date(fieldValue as string);
      if (!isNaN(date.getTime())) return date;
    }
    catch
    {
      // ignore
    }
    return null;
  };

  const [selectedDate, setSelectedDate] = useState<Date | null>(() =>
    parseToDate(customField.Value),
  );

  useEffect(() =>
  {
    setSelectedDate(parseToDate(customField.Value));
  }, [customField.Value]);

  useEffect(() =>
  {
    if (autoOpen) datepickerRef.current?.setOpen(true);
  }, []);

  const isFieldDisabled = !canEdit || !customField.ConversationIsEnable;

  const handleChange = (date: Date | null) =>
  {
    setSelectedDate(date);
    if (!date)
    {
      handleDynamicInputChange(customField.CustomFieldId, '');
    }
    else
    {
      handleDynamicInputChange(customField.CustomFieldId, date.toISOString());
    }
  };

  const handleCalendarClose = () =>
  {
    if (UpdateCustomFields)
    {
      UpdateCustomFields(customField);
    }
  };

  return (
    <div className={showRequiredError ? 'submit-required-error-wrapper' : undefined}>
      <DatePicker
        ref={datepickerRef}
        selected={selectedDate}
        onChange={handleChange}
        onCalendarClose={handleCalendarClose}
        dateFormat="MM/dd/yyyy"
        placeholderText={customField.Placeholder || 'MM/DD/YYYY'}
        disabled={isFieldDisabled}
        className="corgee-inpuSubmitTimet"
        wrapperClassName="submit-time-datepicker-wrapper"
        customInput={<DateInput disabled={isFieldDisabled} />}
        popperProps={{ strategy: 'fixed' }}
        showPopperArrow={false}
      />
    </div>
  );
};

export default InputDateField;
